import { useState } from "react"

import { type JobSlug } from "@/components/landing/discovery/script"
import { type JobOffer } from "@/components/landing/hub/types"

/**
 * The pinned ask, and the whole purchase surface: collapsed it carries the
 * ONE recommended agent; "See all options" expands the same card into a
 * single-select catalog — no navigation away, the overlay stays the world.
 *
 * IMP-531: mounts at the post-call email ask — the value peak — with the
 * recommended agent and price only. Prices are the partner's real per-job
 * retail prices; the overlay only mounts the bar when at least one job is
 * purchasable.
 */
export function PurchaseBar({
  jobs,
  recommended,
  onGetStarted,
}: {
  /** Purchasable jobs only (pricing present), in hub order. */
  jobs: JobOffer[]
  recommended: JobSlug
  onGetStarted: (job: JobOffer) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [selected, setSelected] = useState(
    () => jobs.find((job) => job.slug === recommended)?.slug ?? jobs[0]?.slug,
  )

  const chosen = jobs.find((job) => job.slug === selected) ?? jobs[0]
  if (!chosen?.pricing) return null

  const dollars = (cents: number) => `$${Math.round(cents / 100)}`

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-60 pb-4">
      <div className="pointer-events-auto mx-auto w-full max-w-2xl px-4">
        <div className="lp-msg-in border-lp-border flex flex-col gap-3 rounded-2xl border bg-white p-3 pl-5 shadow-lg">
          {expanded && (
            <div className="border-lp-border flex flex-col gap-2 border-b pt-2 pr-2 pb-3">
              {jobs.map((job) => {
                const active = selected === job.slug
                return (
                  <button
                    key={job.slug}
                    type="button"
                    onClick={() => setSelected(job.slug)}
                    aria-pressed={active}
                    className={
                      active
                        ? "lp-card-selected flex items-center justify-between rounded-xl border px-4 py-2.5 text-left transition-colors"
                        : "border-lp-border hover:border-lp-border-strong flex items-center justify-between rounded-xl border px-4 py-2.5 text-left transition-colors"
                    }
                  >
                    <span className="text-lp-fg text-[14px] font-medium">
                      {job.name}
                      {job.slug === recommended && (
                        <span className="text-lp-muted ml-2 text-[12px] font-normal">
                          Recommended
                        </span>
                      )}
                    </span>
                    <span className="text-lp-muted text-[13px] tabular-nums">
                      {job.pricing
                        ? `${dollars(job.pricing.retail_price_cents)}/mo`
                        : ""}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="min-w-0 flex-1">
              <p className="text-lp-fg text-[14px] font-medium">
                {chosen.name}
              </p>
              <p className="text-lp-muted text-[12.5px]">
                {/* IMP-531: price only — no missed-revenue claim, which this
                    flow never earns (it collects no numbers). */}
                {`${dollars(chosen.pricing.retail_price_cents)}/mo, trained on your business`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              className="text-lp-muted hover:text-lp-fg text-[13px] font-medium transition-colors"
            >
              {expanded ? "Hide options" : "See all options"}
            </button>
            <button
              type="button"
              onClick={() => onGetStarted(chosen)}
              className="bg-lp-fg h-12 shrink-0 rounded-full px-7 text-[15px] font-semibold text-white transition-colors hover:bg-black"
            >
              Get started
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
